const GTWIN_API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GTWIN_API_URL
    ? String(import.meta.env.VITE_GTWIN_API_URL).replace(/\/$/, '')
    : '') ||
  (typeof window !== 'undefined'
    ? `${window.location.origin}/gtwin-api`
    : '/gtwin-api');

export interface TenantRecord {
  id: string;
  code: string;
  label: string;
  region: string | null;
  classification: string;
  units: string;
  estate_gtwin_id: string | null;
}

export interface AssetShellRecord {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  label: string;
  lor: number;
  node_type: string;
  domain_profile: string | null;
  path: string | null;
  gtwin_id: string | null;
  layer_refs: unknown[];
  bbox: number[] | null;
  representation: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
}

export interface AssetTreeNode extends AssetShellRecord {
  children: AssetTreeNode[];
}

function headers(tenantId?: string): Record<string, string> {
  const h: Record<string, string> = { accept: 'application/json', 'content-type': 'application/json' };
  if (tenantId) h['X-Tenant-Id'] = tenantId;
  return h;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function listTenants(): Promise<TenantRecord[]> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants`, { headers: headers() });
  return parseJson(res);
}

export async function createTenant(input: {
  code: string;
  label: string;
  region?: string;
  classification?: string;
  units?: string;
  created_by?: string;
}): Promise<TenantRecord> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      code: input.code,
      label: input.label,
      region: input.region ?? null,
      classification: input.classification ?? 'internal',
      units: input.units ?? 'metric',
      created_by: input.created_by ?? 'estate-admin',
    }),
  });
  return parseJson(res);
}

export async function fetchAssetTree(tenantId: string): Promise<AssetTreeNode[]> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets/tree`, {
    headers: headers(tenantId),
  });
  return parseJson(res);
}

export async function createAsset(
  tenantId: string,
  input: {
    label: string;
    lor: number;
    parent_id?: string | null;
    node_type?: string;
    bbox?: number[] | null;
    representation?: Record<string, unknown> | null;
  },
): Promise<AssetShellRecord> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets`, {
    method: 'POST',
    headers: headers(tenantId),
    body: JSON.stringify(input),
  });
  return parseJson(res);
}

export async function getAsset(tenantId: string, assetId: string): Promise<AssetShellRecord> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets/${assetId}`, {
    headers: headers(tenantId),
  });
  return parseJson(res);
}

export async function updateAsset(
  tenantId: string,
  assetId: string,
  input: Partial<{
    label: string;
    lor: number;
    parent_id: string | null;
    node_type: string;
    domain_profile: string | null;
    path: string | null;
    gtwin_id: string | null;
    layer_refs: unknown[];
    bbox: number[] | null;
    representation: Record<string, unknown> | null;
    metadata: Record<string, unknown>;
  }>,
): Promise<AssetShellRecord> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets/${assetId}`, {
    method: 'PATCH',
    headers: headers(tenantId),
    body: JSON.stringify(input),
  });
  return parseJson(res);
}

export async function associateAssetLayer(
  tenantId: string,
  assetId: string,
  input: {
    gtwinId: string;
    sceneItemId?: string | null;
    slot?: string | null;
    layerKind?: string | null;
    sourceRunId?: string | null;
    label?: string | null;
    metadata?: Record<string, unknown> | null;
  },
): Promise<AssetShellRecord> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets/${assetId}/layer-associations`, {
    method: 'POST',
    headers: headers(tenantId),
    body: JSON.stringify(input),
  });
  return parseJson(res);
}

export async function listLor3Assets(tenantId: string): Promise<AssetShellRecord[]> {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/tenants/${tenantId}/assets/lor3`, {
    headers: headers(tenantId),
  });
  return parseJson(res);
}

export async function fetchScopeManifest(tenantId: string, gtwinId: string) {
  const res = await fetch(`${GTWIN_API_BASE}/api/v1/gtwins/${gtwinId}/scope-manifest`, {
    headers: headers(tenantId),
  });
  return parseJson(res);
}

export { GTWIN_API_BASE };
